import { useEffect, useState } from 'react';
import { C } from '../lib/theme';
import { styles } from '../lib/styles';
import { fmtDateTime } from '../lib/util';
import { getCurrentUser } from '../auth/useUser';
import { AIA_ACTIONS, startAccountAction } from '../auth/keycloak';
import { ApiProblem } from '../api/client';
import {
  fetchMyActivity,
  fetchSignInHistory,
  type ActivityEntry,
  type SignInEvent,
} from '../api/preferences';
import { roleLabel } from '../components/ProfileMenu';

// #399 — "My profile": who you are here, what your role can do, where you have
// signed in from, what you have done, and the two self-service credential
// actions. Read-only apart from those two buttons — name, email and role are
// owned by Keycloak (and, for federated staff, by Entra), so an editable field
// here would be a lie the next login overwrites.

// What each app role can do, in the portal's own terms. Sourced from the RLS
// policies (9999_security.sql) and the route guards, not invented: these are
// the sentences a user needs to answer "why can't I approve this?".
const ROLE_CAPABILITIES: Record<string, string[]> = {
  ADMIN: [
    'See and edit every case',
    'Record decisions and final determinations',
    'Manage portal users, customers, and API credentials',
    'Run Settlement Platform operations and read the full audit trail',
  ],
  APPROVER: [
    'See and edit every case',
    'Approve decisions — but never on a case you are the OA/VA Agent for (dual control)',
    'Read the full audit trail',
  ],
  ANALYST: [
    'See and edit every case',
    'Work the vetting queue and record findings',
    'Cannot approve a case you are the OA/VA Agent for (dual control)',
  ],
  LEGAL: [
    'See only cases flagged for legal hold',
    'Record legal process and final determinations on those cases',
  ],
};

function Panel({ title, children, action }: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

/**
 * Renders whichever of loading / unavailable / empty / content applies.
 *
 * The `unavailable` case is the point of this helper. "We are not permitted to
 * read your sign-in history" and "you have never signed in" look identical if
 * both render as an empty table, and they are very different things to tell
 * somebody who is checking their account after a suspicious email.
 */
function AsyncPanel<T>({ state, empty, children }: {
  state: { loading: boolean; error: string | null; items: T[] };
  empty: string;
  children: (items: T[]) => React.ReactNode;
}) {
  if (state.loading) return <div style={{ fontSize: 12, color: C.textMuted }}>Loading…</div>;
  if (state.error) {
    return (
      <div style={{ fontSize: 12, color: C.warn, background: C.warnBg, padding: 12, borderRadius: 8 }}>
        {state.error}
      </div>
    );
  }
  if (state.items.length === 0) return <div style={{ fontSize: 12, color: C.textMuted }}>{empty}</div>;
  return <>{children(state.items)}</>;
}

interface AsyncState<T> { loading: boolean; error: string | null; items: T[] }

function useAsyncList<T>(load: () => Promise<{ items: T[] }>): AsyncState<T> {
  const [state, setState] = useState<AsyncState<T>>({ loading: true, error: null, items: [] });
  useEffect(() => {
    let active = true;
    load()
      .then(res => { if (active) setState({ loading: false, error: null, items: res.items }); })
      .catch((err: unknown) => {
        if (!active) return;
        // An ApiProblem's `detail` is written for the reader — for the
        // sign-in feed it names the exact Keycloak role an operator has to
        // grant. Surfacing it beats a generic "failed to load".
        const message = err instanceof ApiProblem
          ? (err.detail ?? err.title)
          : err instanceof Error ? err.message : String(err);
        setState({ loading: false, error: message, items: [] });
      });
    return () => { active = false; };
    // Loaders are module-level functions with stable identity; this runs once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

export function Profile() {
  const user = getCurrentUser()!;
  const signIns = useAsyncList<SignInEvent>(() => fetchSignInHistory(20));
  const activity = useAsyncList<ActivityEntry>(() => fetchMyActivity(20));
  const role = roleLabel(user);

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>My Profile</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Panel title="Account">
          <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 }}>
            <dt style={{ color: C.textMuted }}>Name</dt>
            <dd style={{ margin: 0, color: C.text }}>{user.name || '—'}</dd>
            <dt style={{ color: C.textMuted }}>Email</dt>
            <dd style={{ margin: 0, color: C.text, wordBreak: 'break-all' }}>{user.email || '—'}</dd>
            <dt style={{ color: C.textMuted }}>Role</dt>
            <dd style={{ margin: 0, color: C.text }}>{role || '—'}</dd>
          </dl>
          <p style={{ fontSize: 11, color: C.textMuted, marginTop: 12, marginBottom: 0 }}>
            Name, email, and role come from your sign-in account. Ask an administrator to change them.
          </p>
        </Panel>

        <Panel title="What this role can do">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.9, color: C.text }}>
            {(ROLE_CAPABILITIES[role] ?? ['No portal role is assigned to this account.']).map(line => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </Panel>

        <Panel title="Password and authenticator">
          <p style={{ fontSize: 12, color: C.textMuted, marginTop: 0 }}>
            These open the sign-in service and return you here when you are done.
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
              onClick={() => startAccountAction(AIA_ACTIONS.changePassword)}
            >
              Change password
            </button>
            <button
              type="button"
              style={{ ...styles.btn, ...styles.btnOutline, ...styles.btnSmall }}
              onClick={() => startAccountAction(AIA_ACTIONS.configureTotp)}
            >
              Reconfigure authenticator
            </button>
          </div>
          {/* The asymmetry is worth stating: you cannot self-reset MFA you
              cannot sign in with, so a lost device stays an admin flow. */}
          <p style={{ fontSize: 11, color: C.textMuted, marginBottom: 0, marginTop: 12 }}>
            Lost your authenticator device and cannot sign in? An administrator has to reset it for you.
          </p>
        </Panel>

        <Panel title="Recent sign-ins">
          <AsyncPanel state={signIns} empty="No sign-in events recorded.">
            {items => (
              <table style={styles.table}>
                <thead><tr>
                  <th style={styles.th}>When</th>
                  <th style={styles.th}>Result</th>
                  <th style={styles.th}>From</th>
                </tr></thead>
                <tbody>
                  {items.map((e, i) => {
                    const failed = e.type === 'LOGIN_ERROR';
                    return (
                      <tr key={`${e.time}-${i}`}>
                        <td style={styles.td}>{fmtDateTime(e.time)}</td>
                        <td style={{ ...styles.td, color: failed ? C.danger : C.success }}>
                          {failed ? `Failed${e.error ? ` (${e.error})` : ''}` : 'Signed in'}
                        </td>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{e.ipAddress ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </AsyncPanel>
        </Panel>
      </div>

      <Panel title="My recent activity">
        <AsyncPanel state={activity} empty="You have not recorded any activity yet.">
          {items => (
            <table style={styles.table}>
              <thead><tr>
                <th style={styles.th}>When</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Case</th>
              </tr></thead>
              <tbody>
                {items.map(a => (
                  <tr key={a.id}>
                    <td style={styles.td}>{fmtDateTime(a.timestamp)}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: 11 }}>{a.action}</td>
                    <td style={styles.td}>
                      {a.registrant ?? <span style={{ color: C.textMuted }}>{a.caseId}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </AsyncPanel>
      </Panel>
    </div>
  );
}
